import type { CSSProperties } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { INTERAX_COLORS } from '../../../../constants/colors'

// Sizes and styles every Control Tower card shares.

const META_ICON_SIZE_PX = 16 // 16 px
const TILE_ICON_SIZE_PX = 22 // 22 px
const BUTTON_ICON_SIZE_PX = 16 // 16 px
const HEADER_ROW_MIN_HEIGHT_PX = 32 // the menu button's height, so cards without one line up
const STATUS_DOT_SIZE_PX = 8 // 8 px
const ROSTER_AVATAR_SIZE_PX = 28 // 28 px
const SIGNAL_ICON_SIZE_PX = 20 // 20 px
const SIGNAL_COLUMN_WIDTH_PX = 88 // 88 px
// A whole sprint's roster shows four people and scrolls the rest inside its card.
export const SPRINT_CALL_ROSTER_VISIBLE_ROWS = 4

export const controlTowerMetaIconStyle: CSSProperties = { width: META_ICON_SIZE_PX, height: META_ICON_SIZE_PX }
export const controlTowerTileIconStyle: CSSProperties = { width: TILE_ICON_SIZE_PX, height: TILE_ICON_SIZE_PX }
export const controlTowerButtonIconStyle: CSSProperties = { width: BUTTON_ICON_SIZE_PX, height: BUTTON_ICON_SIZE_PX }

export const controlTowerCardSx: SxProps<Theme> = {
  borderRadius: '18px',
  p: { xs: 1.5, sm: 2, md: 2 },
  backgroundColor: INTERAX_COLORS.white04,
  border: `1px solid ${INTERAX_COLORS.white10}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  minWidth: 0
}

export const controlTowerHeaderRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  flexWrap: 'wrap',
  minHeight: HEADER_ROW_MIN_HEIGHT_PX
}

// The kind pill and the card's title, side by side.
export const controlTowerTitleGroupSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  minWidth: 0
}

// A card's action button, on its own row at the left.
export const controlTowerActionRowSx: SxProps<Theme> = { display: 'flex', justifyContent: 'flex-start' }

export const controlTowerTitleSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white,
  fontWeight: 700,
  fontSize: '18px',
  lineHeight: '24px'
}

export const controlTowerActivityTitleSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white70,
  fontSize: '14px',
  lineHeight: '20px',
  mt: -0.5
}

export const controlTowerMetaRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  flexWrap: 'wrap',
  pb: 1.5,
  borderBottom: `1px solid ${INTERAX_COLORS.white10}`
}

export const controlTowerMetaItemSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  color: INTERAX_COLORS.white70
}

export const controlTowerMetaTextSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white,
  fontSize: '14px',
  lineHeight: '20px'
}

export const buildControlTowerPillSx = (backgroundColor: string): SxProps<Theme> => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  px: 1.25,
  py: 0.5,
  borderRadius: '999px',
  backgroundColor,
  flexShrink: 0
})

export const controlTowerKindLabelSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white,
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px'
}

export const buildControlTowerStatusDotSx = (color: string): SxProps<Theme> => ({
  width: STATUS_DOT_SIZE_PX,
  height: STATUS_DOT_SIZE_PX,
  borderRadius: '50%',
  backgroundColor: color
})

export const buildControlTowerStatusLabelSx = (color: string): SxProps<Theme> => ({
  color,
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: '16px'
})

export const controlTowerSignalIconStyle: CSSProperties = { width: SIGNAL_ICON_SIZE_PX, height: SIGNAL_ICON_SIZE_PX }

export const controlTowerRosterFrameSx: SxProps<Theme> = {
  borderRadius: '12px',
  border: `1px solid ${INTERAX_COLORS.white10}`,
  overflow: 'hidden'
}

// The header's tint and rule sit on its box, so they reach the frame's edge over the scrollbar's room too.
const rosterHeaderSx = {
  backgroundColor: INTERAX_COLORS.white04,
  borderBottom: `1px solid ${INTERAX_COLORS.white10}`
}

// Both keep room for the scrollbar, so the header's columns stay over the rows' columns.
export const buildControlTowerRosterHeaderSx = (isScrollable: boolean): SxProps<Theme> =>
  isScrollable ? { ...rosterHeaderSx, overflowY: 'hidden', scrollbarGutter: 'stable' } : rosterHeaderSx

// A scrolling roster stops at the height of its first rows and scrolls the rest.
export const buildControlTowerRosterRowsSx = (isScrollable: boolean, maxHeightPx?: number): SxProps<Theme> =>
  isScrollable ? { maxHeight: maxHeightPx, overflowY: 'auto', scrollbarGutter: 'stable' } : {}

export const controlTowerSignalColumnSx: SxProps<Theme> = { width: SIGNAL_COLUMN_WIDTH_PX }

// Fixed columns: a long name or team is cut short instead of squeezing the signal columns.
export const controlTowerRosterTableSx: SxProps<Theme> = {
  tableLayout: 'fixed',
  '& td:last-of-type, & th:last-of-type': { borderRight: 0 }
}

export const controlTowerRosterHeaderCellSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white70,
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
  borderBottom: 0,
  py: 1,
  px: 1.5
}

export const controlTowerRosterBodyCellSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white,
  fontSize: '14px',
  lineHeight: '20px',
  borderBottom: `1px solid ${INTERAX_COLORS.white10}`,
  py: 0.75,
  px: 1.5
}

export const controlTowerRosterPersonSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  minWidth: 0
}

// The team sits beside the name, not under it, so every card's rows are the same height.
export const controlTowerRosterNameLineSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 1,
  minWidth: 0
}

export const controlTowerRosterNameSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white,
  fontSize: '14px',
  lineHeight: '20px',
  flexShrink: 0,
  maxWidth: '100%'
}

// The team takes what the name leaves, and is cut short first.
export const controlTowerRosterTeamSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white70,
  fontSize: '12px',
  lineHeight: '16px',
  minWidth: 0
}

export const buildControlTowerAvatarSx = (backgroundColor: string): SxProps<Theme> => ({
  width: ROSTER_AVATAR_SIZE_PX,
  height: ROSTER_AVATAR_SIZE_PX,
  fontSize: '11px',
  fontWeight: 700,
  color: INTERAX_COLORS.offBlack,
  backgroundColor
})
